import { useCallback, useEffect, useState } from "react";

export type Resource<T> = {
  status: "loading" | "ready" | "error";
  data: T | null;
  retry: () => void;
};
export const homepageApi = process.env.REACT_APP_API_URL || "http://localhost:5001";
export const feedbackApi = process.env.REACT_APP_PUBLIC_FEEDBACK_API_URL || process.env.REACT_APP_API_URL || "http://localhost:5001";

// Each public resource has its own request, timeout and retry. One failed bank
// must never erase the available bank or prevent the visitor from browsing it.
export function useHomepageResource<T>(url: string, validate: (data: unknown) => T): Resource<T> {
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<{ status: Resource<T>["status"]; data: T | null }>({ status: "loading", data: null });
  const retry = useCallback(() => setAttempt(value => value + 1), []);
  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 15000);
    setState({ status: "loading", data: null });
    fetch(url, { signal: controller.signal })
      .then(async response => { if (!response.ok) throw new Error(`HTTP ${response.status}`); return validate(await response.json()); })
      .then(data => { if (active) setState({ status: "ready", data }); })
      .catch(error => {
        if (!active) return;
        if (process.env.NODE_ENV === "development") console.warn("Homepage data unavailable", { url, reason: controller.signal.aborted ? "timeout" : String(error?.message || error) });
        setState({ status: "error", data: null });
      })
      .finally(() => window.clearTimeout(timeout));
    return () => { active = false; controller.abort(); window.clearTimeout(timeout); };
  }, [url, validate, attempt]);
  return { ...state, retry };
}
